import parse from 'html-react-parser';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { CommentCard } from './CommentCard';
import { CommentEditor } from './CommentEditor';
import { Comment } from './types';
import { makeStyles } from '@material-ui/core';
import { useHighlighting } from '../Highlighting';
import { usePrecloseHandlers } from './hooks';
import type { MentionableProps } from './Mentionable';

interface Props {
  comment?: null | Comment;
  draft?: string;
  drafting?: boolean;
  author: string;
  mentionableProps?: null | Omit<MentionableProps, 'target'>;
  text: {
    submit: ReactNode;
    cancel: ReactNode;
  };
  testIds: {
    submitButton: string;
    cancelButton: string;
  };
  handlers: {
    onSubmit(body: string, attributions: string[]): unknown;
    onCancel(): unknown;
  };
  slots: {
    commentCardAction: ReactNode;
  };
}

const useStyles = makeStyles(() => ({
  highlightedComment: {
    backgroundColor: 'rgba(28, 148, 185, 0.12)',
  },
}));

export function CommentableComment({
  author,
  comment = null,
  drafting = false,
  text,
  testIds,
  handlers,
  slots,
  mentionableProps = null,
}: Props) {
  const classes = useStyles();
  const { registerPrecloseHandler } = usePrecloseHandlers();
  const [body, setBody] = useState(comment?.body ?? '');
  const [attributions, setAttributions] = useState(comment?.attributions ?? []);
  const initialBody = useRef(comment?.body ?? '');
  const { highlighted } = useHighlighting();
  const bodyWithHighlighting =
    comment?.body.replace(
      new RegExp(`(?<!</?[^>]*|&[^;]*)(${highlighted})`, 'gi'),
      (match) => `<span class="${classes.highlightedComment}">${match}</span>`,
    ) ?? '';

  useEffect(() => {
    if (body !== initialBody.current) {
      const unregister = registerPrecloseHandler(() => handlers.onSubmit(body, attributions));

      return () => {
        unregister();
      };
    }
  });

  useEffect(() => {
    if (comment?.body && comment.body !== initialBody.current) {
      initialBody.current = comment.body;
      setBody(comment.body);
    }
  }, [comment]);

  function handleAddAttribution(attribution: string) {
    setAttributions((prev) => prev.concat(attribution));
  }

  function handleRemoveAttribution(attribution: string) {
    setAttributions((prev) => {
      const next = [...prev];
      const index = next.findIndex((each) => each === attribution);

      if (index > -1) {
        next.splice(index, 1);
      }

      return next;
    });
  }

  return (
    <CommentCard
      author={author}
      updatedAt={comment?.updatedTimestamp}
      headerAction={slots.commentCardAction}
      id={comment?.id}
    >
      {drafting ? (
        <CommentEditor
          value={body}
          text={text}
          testIds={testIds}
          handlers={{
            onChange: setBody,
            onSubmit: () => handlers.onSubmit(body, attributions),
            onCancel: handlers.onCancel,
          }}
          mentionableProps={{
            status: 'loading',
            users: [],
            ...mentionableProps,
            onMentionUser: handleAddAttribution,
            onUnmentionUser: handleRemoveAttribution,
          }}
        />
      ) : (
        parse(bodyWithHighlighting)
      )}
    </CommentCard>
  );
}
